/**
 * SecurityService - API client for security (password gate, DEK status, set, reset).
 */

import { API_BASE_URL } from '../config/apiConfig';

const SECURITY_API_URL = `${API_BASE_URL}/security`;

function getAuthToken(): string | null {
  return sessionStorage.getItem('authToken');
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export interface DekStatusResponse {
  success: boolean;
  dekSet?: boolean;
  message?: string;
}

export interface VerifyPasswordResponse {
  success: boolean;
  message?: string;
}

export async function verifyPassword(password: string): Promise<VerifyPasswordResponse> {
  const res = await fetch(`${SECURITY_API_URL}/verify-password`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ password }),
  });
  return res.json();
}

export async function getDekStatus(): Promise<DekStatusResponse> {
  const res = await fetch(`${SECURITY_API_URL}/dek/status`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return res.json();
}

export interface SetDekResponse {
  success: boolean;
  message?: string;
}

export async function setDek(password: string, part1: string, part2: string, part3: string): Promise<SetDekResponse> {
  const res = await fetch(`${SECURITY_API_URL}/dek/set`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ password, part1, part2, part3 }),
  });
  return res.json();
}

export interface ResetDekPayload {
  accountPassword: string;
  oldDekPart1: string;
  oldDekPart2: string;
  oldDekPart3: string;
  newDekPart1: string;
  newDekPart2: string;
  newDekPart3: string;
}

export interface ResetDekResponse {
  success: boolean;
  message?: string;
}

export async function resetDek(payload: ResetDekPayload): Promise<ResetDekResponse> {
  const res = await fetch(`${SECURITY_API_URL}/dek/reset`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      password: payload.accountPassword,
      oldPart1: payload.oldDekPart1,
      oldPart2: payload.oldDekPart2,
      oldPart3: payload.oldDekPart3,
      newPart1: payload.newDekPart1,
      newPart2: payload.newDekPart2,
      newPart3: payload.newDekPart3,
    }),
  });
  return res.json();
}

// --- Admin account recovery (recovery codes + 3 custom security questions) ---

export interface RecoveryStatusResponse {
  success: boolean;
  configured?: boolean;
  question1?: string;
  question2?: string;
  question3?: string;
  message?: string;
}

export interface RecoverySetupPayload {
  recoveryId: string;
  recoveryPin: string;
  question1: string;
  question2: string;
  question3: string;
  answer1: string;
  answer2: string;
  answer3: string;
}

export interface RecoverySetupResponse {
  success: boolean;
  message?: string;
}

export async function getRecoveryStatus(): Promise<RecoveryStatusResponse> {
  const res = await fetch(`${SECURITY_API_URL}/recovery/status`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return res.json();
}

export async function saveRecoverySetup(payload: RecoverySetupPayload): Promise<RecoverySetupResponse> {
  const res = await fetch(`${SECURITY_API_URL}/recovery/setup`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  return res.json();
}
